import { get, includes, isEmpty, intersection } from 'lodash';
import dayjs from 'dayjs';

import { GC_ROLES, ROLE_PRIORITY } from './constants';
import { isKnownNonProductionEnvironment, isLocalDev } from './environment';

export const downloadBlob = (blob, fileName) => {
  const anchor = document.createElement('a');
  document.body.appendChild(anchor);
  const objectUrl = window.URL.createObjectURL(blob);
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(objectUrl);
  anchor.parentNode.removeChild(anchor);
};

// TASK-011 fix: this used to compare `window.top.location` (a Location object) to the string
// 'testlis.tll.aero' - a comparison that can never be true (a Location is never `===` a string),
// so the TEST-environment branch this was meant to enable was silently always false. `isDev()`
// now asks `app/environment.js` instead: true on the local dev server (`npm start`) exactly as
// before, AND now also true on any deployment whose `APP_ENVIRONMENT` is one of a closed list of
// recognized non-production names (TEST, DEV, DEVELOPMENT, STAGING, QA, LOCAL -
// `isKnownNonProductionEnvironment`, not the header pill's more permissive rule) - see that
// module for the exact list. A typo or rename on a real LIVE deployment (`PRD`, `LIEV`,
// `prod-ee`, ...) must not silently turn on the debug logging this gates. TASK-009: everything
// this gates was audited and made safe to run on TEST too (no token/PII logging, debug UI
// restricted further where it still needs to stay local-only) - see the call sites `isDev()` gates.
export const isDev = () => {
  return isLocalDev() || isKnownNonProductionEnvironment();
};

export const isAdminUser = (idTokenClaims) => {
  const userRoles = get(idTokenClaims, 'roles', []);
  return includes(userRoles, 'Admins');
};

export const isUserAllowed = (idTokenClaims) => {
  if (isEmpty(idTokenClaims)) {
    return false;
  }

  const userRoles = get(idTokenClaims, 'roles', []);
  if (!userRoles || isEmpty(intersection(ROLE_PRIORITY, userRoles))) {
    return false;
  }

  // Accounts for the newer roles (TeamManager, GuestPermit, Instructor) must carry this claim too.
  if (!get(idTokenClaims, 'companyname')) {
    return false;
  }

  return true;
};

// Whether the user holds a role admitted to the /gc guest-checkpoint desk (Admins or
// GuestPermit — see constants.GC_ROLES). Checks the held roles, not the active one: /gc/* calls
// authorize under a held GC role regardless of which role the picker currently has active.
export const holdsGcRole = (idTokenClaims) => {
  const userRoles = get(idTokenClaims, 'roles', []);
  return !isEmpty(intersection(GC_ROLES, userRoles));
};

export const displayDate = (date) => {
  if (!date) return '';

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return null;
  return dayjsDate.format('DD.MM.YYYY');
};

const DATE_PREFIX = /^\d{4}-\d{2}-\d{2}/;
const DATETIME_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z$/;

/**
 * Normalises a calendar date (a birthday) to the plain `YYYY-MM-DD` string — the one wire/UI
 * format that carries no time-of-day and therefore cannot shift by a day with the timezone:
 * `<input type="date">` only accepts exactly this shape, and the backend takes it for a
 * `DateTime?` and prints the calendar date as it was sent.
 *
 *  - a dayjs instance (a date-picker value) -> its date in the browser's timezone, i.e. the day the
 *    user actually picked (`null` if invalid);
 *  - a UTC instant string ending in `Z` (what a dayjs picker value becomes once JSON-serialised,
 *    e.g. `1990-01-01T22:00:00.000Z` for the 2nd in Tallinn) -> its date in the browser's
 *    timezone, i.e. the only case that goes through dayjs/local time: cutting that string to its
 *    first 10 characters would be a day off;
 *  - any other string that starts with `YYYY-MM-DD` — date-only, a datetime with an explicit offset
 *    (`1990-01-02T00:00:00+03:00`, how the backend serialises its `DateTime?`; `+hhmm` too) or no
 *    zone at all (`1990-01-02T00:00:00`) -> exactly those first 10 characters: the calendar date
 *    as written. Deliberately NOT round-tripped through dayjs/`Date`, which would give the previous
 *    day in any timezone west of the offset;
 *  - anything else (`null`, `undefined`, `''`, garbage, other types) -> `null`.
 *
 * @param {unknown} value
 * @returns {string | null}
 */
export const toDateOnly = (value) => {
  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.format('YYYY-MM-DD') : null;
  }
  if (typeof value !== 'string') return null;

  if (DATETIME_UTC.test(value)) {
    const instant = dayjs(value);
    return instant.isValid() ? instant.format('YYYY-MM-DD') : null;
  }
  const match = DATE_PREFIX.exec(value);
  return match ? match[0] : null;
};

export const displayDateTime = (date) => {
  if (!date) return '';

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return null;
  return dayjsDate.format('DD.MM.YYYY HH:mm');
};

const hexToRgb = (hex) => {
  let r = 0,
    g = 0,
    b = 0;
  // 3 digits
  if (hex.length === 4) {
    r = '0x' + hex[1] + hex[1];
    g = '0x' + hex[2] + hex[2];
    b = '0x' + hex[3] + hex[3];
  } else if (hex.length === 7) {
    // 6 digits
    r = '0x' + hex[1] + hex[2];
    g = '0x' + hex[3] + hex[4];
    b = '0x' + hex[5] + hex[6];
  }
  return { r, g, b };
};

const luminance = (r, g, b) => {
  const a = [r, g, b].map(function (v) {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

export const getVisibleTextColor = (hex) => {
  const rgb = hexToRgb(hex);
  const lum = luminance(rgb.r, rgb.g, rgb.b);
  return lum > 0.179 ? 'black' : 'white';
};
